'use client';

import { datadogRum } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { datadogLogs } from '@datadog/browser-logs';

let initialized = false;

type TraceContext = {
  trace_id?: string;
  trace_id_hex?: string;
  session_span_id?: string;
  phase_span_id?: string;
  phase?: string;
};

type DatadogGameContext = {
  mode?: string | null;
  roomName?: string | null;
  gameId?: string | null;
  gameSessionId?: string | null;
  hubSessionId?: string | null;
  phase?: string | null;
  traceContext?: TraceContext | null;
};

const GAME_CONTEXT_KEYS = [
  'game.mode',
  'game.room',
  'game.id',
  'game.session_id',
  'game.hub_session_id',
  'game.phase',
  'game.trace_id',
  'game.trace_id_hex',
  'game.session_span_id',
  'game.phase_span_id',
];

/**
 * Detect which service context we're running in based on URL path.
 */
function detectService(): string {
  if (typeof window === 'undefined') return 'ai-realtime-game';
  const path = window.location.pathname;
  if (path.startsWith('/tv')) return 'ai-realtime-game-tv';
  if (path.startsWith('/remote')) return 'ai-realtime-game-remote';
  return 'ai-realtime-game';
}

export function initDatadogRum() {
  if (initialized || typeof window === 'undefined') return;

  try {
    datadogRum.init({
      applicationId: 'cf59fdbb-106e-4213-9bab-4f5e908c4f01',
      clientToken: 'pub2fc7844b40e8003811052a1f37387025',
      site: 'datadoghq.com',
      service: detectService(),
      env: process.env.NEXT_PUBLIC_VERCEL_ENV || 'development',
      version: process.env.NEXT_PUBLIC_COMMIT_SHA,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      defaultPrivacyLevel: 'mask-user-input',
      plugins: [reactPlugin({ router: false })],
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      allowedTracingUrls: [window.location.origin],
      traceSampleRate: 100,
    });

    initialized = true;
  } catch (err) {
    // Swallow errors to avoid breaking the app if RUM fails
    console.warn('Datadog RUM failed to initialize', err);
  }
}

function setGlobalContextProperty(key: string, value: unknown) {
  try {
    if (value === undefined || value === null || value === '') {
      datadogRum.removeGlobalContextProperty(key);
      datadogLogs.removeGlobalContextProperty(key);
      return;
    }
    datadogRum.setGlobalContextProperty(key, value);
    datadogLogs.setGlobalContextProperty(key, value);
  } catch {
    // Context helpers should never affect gameplay.
  }
}

export function setDatadogGameContext(context: DatadogGameContext) {
  const values: Record<string, unknown> = {};
  if ('mode' in context) values['game.mode'] = context.mode;
  if ('roomName' in context) values['game.room'] = context.roomName;
  if ('gameId' in context) values['game.id'] = context.gameId;
  if ('gameSessionId' in context) values['game.session_id'] = context.gameSessionId;
  if ('hubSessionId' in context) values['game.hub_session_id'] = context.hubSessionId;
  // Phase precedence:
  //   - `phase: '<string>'` → use the string
  //   - `phase: null`       → explicit clear (wins over traceContext.phase)
  //   - `phase: undefined` or key absent → fall back to traceContext.phase
  // Most callers always include `phase: parsed.phase` in the object literal,
  // so `'phase' in context` is true even when the value is `undefined`; we
  // therefore distinguish `null` (clear) from `undefined` (fall through).
  if ('phase' in context && context.phase !== undefined) {
    values['game.phase'] = context.phase;
  }
  if ('traceContext' in context) {
    if (!('game.phase' in values)) {
      values['game.phase'] = context.traceContext?.phase;
    }
    values['game.trace_id'] = context.traceContext?.trace_id;
    values['game.trace_id_hex'] = context.traceContext?.trace_id_hex;
    values['game.session_span_id'] = context.traceContext?.session_span_id;
    values['game.phase_span_id'] = context.traceContext?.phase_span_id;
  }

  for (const key of GAME_CONTEXT_KEYS) {
    if (key in values) {
      setGlobalContextProperty(key, values[key]);
    }
  }
}

export function trackDatadogAction(name: string, context?: Record<string, unknown>) {
  try {
    datadogRum.addAction(name, context);
  } catch {
    // Ignore RUM failures.
  }
}

export function trackDatadogError(error: unknown, context?: Record<string, unknown>) {
  try {
    datadogRum.addError(error instanceof Error ? error : new Error(String(error)), context);
  } catch {
    // Ignore RUM failures.
  }
}
